// component-mascot.ts — Mascotte « Professeur Ornaxion » (affichage non intrusif).
//
// Deux formats configurables par tenant (`display_style`) :
//   - 'peek'  : le Prof « passe la tête » depuis le bord droit (défaut).
//   - 'toast' : bulle discrète dans le coin bas-droit (auto-disparition).
// Fermeture rapide : ×, Échap, clic ailleurs, ou auto-disparition.
// 100 % déterministe (aucun token IA). Garde d'apparition + tirage : cf.
// domain/mascot-decision.ts. État de cooldown en localStorage isolé par tenant.
// Cf. docs/SPEC_MASCOTTE_ORNAXION.md.

import { shouldShowMascot, pickPhrase } from '../domain/mascot-decision.js';
import { buildMascotSvg, MASCOT_KEYFRAMES } from '../domain/mascot-rig.js';

interface MascotSettings {
  display_name: string;
  frequency_percent: number;
  cooldown_minutes: number;
  display_style: 'peek' | 'toast';
  appearance: Record<string, unknown>;
}
interface MascotPhrase {
  id: number;
  trigger_case: string;
  text: string;
  is_system: boolean;
}

const STYLE_ID = 'orn-mascot-style';
const LS_LAST_SHOWN = 'mascot:lastShownAt';
const LS_LAST_PHRASE = 'mascot:lastPhrase:'; // + trigger_case
const AUTO_HIDE_MS = { peek: 10_000, toast: 8_000 };

let settingsCache: MascotSettings | null = null;
let prefEnabledCache: boolean | null = null;

// Élément courant + handlers (pour fermeture propre).
let popEl: HTMLElement | null = null;
let hideTimer: number | null = null;
let onKey: ((e: KeyboardEvent) => void) | null = null;
let onDocClick: ((e: MouseEvent) => void) | null = null;

export function ensureMascotStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = [
    // Conteneur commun
    '.orn-pop{position:fixed;z-index:1200;display:flex;align-items:center;gap:0;',
    'transition:opacity .2s ease,transform .2s ease}',
    '.orn-pop.orn-hide{opacity:0 !important}',
    '.orn-fig{flex:0 0 auto}.orn-fig svg{width:100%;height:auto;display:block}',
    '.orn-bub{position:relative;background:var(--bg2,#fff);border:1px solid var(--border,#e2e8f0);',
    'border-radius:12px;padding:12px 30px 12px 14px;font-size:13px;line-height:1.45;color:var(--text,#1a2540);',
    'box-shadow:0 8px 24px rgba(0,0,0,.15)}',
    '.orn-x{position:absolute;top:5px;right:8px;border:none;background:none;font-size:16px;line-height:1;',
    'cursor:pointer;color:var(--text3,#646e7e)}',
    '.orn-name{font-weight:500;color:var(--accent,#1d9e75);font-size:11px;margin-bottom:3px}',
    // Peek (bord droit)
    '.orn-pop--peek{right:0;top:50%;transform:translateY(-50%)}',
    '.orn-pop--peek .orn-fig{width:104px}',
    '.orn-pop--peek .orn-bub{width:230px}',
    '.orn-pop--peek.orn-in{animation:ornPeekIn .5s cubic-bezier(.2,.85,.3,1.3) both}',
    '@keyframes ornPeekIn{0%{opacity:0;transform:translateY(-50%) translateX(60px)}',
    '70%{opacity:1;transform:translateY(-50%) translateX(-6px)}100%{opacity:1;transform:translateY(-50%) translateX(0)}}',
    '.orn-pop--peek.orn-hide{transform:translateY(-50%) translateX(60px) !important}',
    // Toast (coin bas-droit)
    '.orn-pop--toast{right:20px;bottom:20px}',
    '.orn-pop--toast .orn-fig{width:50px;margin-right:8px}',
    '.orn-pop--toast .orn-bub{width:250px;padding-bottom:16px}',
    '.orn-pop--toast.orn-in{animation:ornToastIn .5s cubic-bezier(.2,.85,.3,1.35) both}',
    '@keyframes ornToastIn{0%{opacity:0;transform:translateY(18px) scale(.94)}',
    '60%{opacity:1;transform:translateY(-4px) scale(1.02)}100%{opacity:1;transform:translateY(0) scale(1)}}',
    '.orn-pop--toast.orn-hide{transform:translateY(18px) !important}',
    '.orn-bar{position:absolute;left:0;bottom:0;height:3px;background:var(--accent,#1d9e75);border-bottom-left-radius:12px}',
    '.orn-pop--toast.orn-in .orn-bar{animation:ornBar 8s linear both}',
    '@keyframes ornBar{from{width:100%}to{width:0}}',
    MASCOT_KEYFRAMES,
  ].join('');
  document.head.appendChild(style);
}

function dismiss(): void {
  if (!popEl) return;
  const el = popEl;
  popEl = null;
  if (hideTimer != null) {
    window.clearTimeout(hideTimer);
    hideTimer = null;
  }
  if (onKey) document.removeEventListener('keydown', onKey, true);
  if (onDocClick) document.removeEventListener('mousedown', onDocClick, true);
  onKey = null;
  onDocClick = null;
  el.classList.add('orn-hide');
  window.setTimeout(() => el.remove(), 260);
}

function render(settings: MascotSettings, phraseText: string): void {
  dismiss(); // ferme une apparition précédente éventuelle
  ensureMascotStyles();
  const style: 'peek' | 'toast' = settings.display_style === 'toast' ? 'toast' : 'peek';
  const name = esc(settings.display_name || 'Professeur Ornaxion');
  const fig = `<div class="orn-fig orn-mascot">${buildMascotSvg(settings.appearance)}</div>`;
  const bubble =
    `<div class="orn-bub"><button class="orn-x" aria-label="${esc(t('common.close'))}">&times;</button>` +
    `<div class="orn-name">${name}</div>${esc(phraseText)}` +
    (style === 'toast' ? '<span class="orn-bar"></span>' : '') +
    `</div>`;

  const el = document.createElement('div');
  el.className = `orn-pop orn-pop--${style}`;
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  // peek : bulle à gauche, figure à droite (contre le bord) ; toast : figure puis bulle.
  el.innerHTML = style === 'peek' ? bubble + fig : fig + bubble;
  document.body.appendChild(el);
  popEl = el;

  el.querySelector('.orn-x')?.addEventListener('click', dismiss);
  // Rejoue l'animation d'entrée.
  void el.offsetWidth;
  el.classList.add('orn-in');

  onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') dismiss();
  };
  onDocClick = (e: MouseEvent) => {
    if (popEl && !popEl.contains(e.target as Node)) dismiss();
  };
  // setTimeout 0 : ne pas capter le clic/keydown qui a mené ici.
  window.setTimeout(() => {
    if (onKey) document.addEventListener('keydown', onKey, true);
    if (onDocClick) document.addEventListener('mousedown', onDocClick, true);
  }, 0);
  hideTimer = window.setTimeout(dismiss, AUTO_HIDE_MS[style]);
}

function readLastShown(): number | null {
  const raw = tlsGet(LS_LAST_SHOWN);
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

function readLastPhrase(triggerCase: string): number | null {
  const raw = tlsGet(LS_LAST_PHRASE + triggerCase);
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

async function getPrefEnabled(): Promise<boolean> {
  if (prefEnabledCache != null) return prefEnabledCache;
  try {
    const r = (await api.getMyMascotPrefs()) as { enabled?: boolean };
    prefEnabledCache = !!r?.enabled;
  } catch {
    prefEnabledCache = false; // en cas d'échec, on n'importune pas l'utilisateur
  }
  return prefEnabledCache;
}

async function getSettings(): Promise<MascotSettings | null> {
  if (settingsCache) return settingsCache;
  try {
    settingsCache = (await api.getMascotSettings()) as MascotSettings;
  } catch {
    settingsCache = null;
  }
  return settingsCache;
}

/** Point d'entrée : appelé après un enregistrement de MAJ d'action. */
async function onActionSaved(resp: unknown): Promise<void> {
  const trig = (resp as { mascot_trigger?: string | null } | null)?.mascot_trigger;
  if (!trig) return;
  await maybeShow(trig);
}

/** Point d'entrée des cas déclenchés par une NAVIGATION (ex. ouverture de la
 * fiche réunion), sans condition métier à évaluer côté serveur : c'est le fait
 * d'arriver sur la page qui mérite le conseil. Les gardes habituelles (mobile,
 * préférence, cooldown, fréquence) s'appliquent pareil. */
async function onPageOpened(triggerCase: string): Promise<void> {
  if (!triggerCase) return;
  await maybeShow(triggerCase);
}

async function maybeShow(triggerCase: string): Promise<void> {
  const isMobile = !!(window.isMobile && window.isMobile());
  if (isMobile) return; // court-circuit avant tout appel réseau

  const prefEnabled = await getPrefEnabled();
  if (!prefEnabled) return;

  const settings = await getSettings();
  if (!settings) return;

  const now = Date.now();
  const decision = shouldShowMascot({
    isMobile,
    prefEnabled,
    cooldownMinutes: settings.cooldown_minutes,
    frequencyPercent: settings.frequency_percent,
    now,
    lastShownAt: readLastShown(),
    rand: Math.random(),
  });
  if (!decision) return;

  let phrases: MascotPhrase[];
  try {
    phrases = (await api.getMascotPhrases(triggerCase)) as MascotPhrase[];
  } catch {
    return;
  }
  const phrase = pickPhrase(phrases, readLastPhrase(triggerCase), Math.random());
  if (!phrase) return;

  render(settings, phrase.text);
  // Le cooldown n'est consommé que sur une apparition réelle.
  tlsSet(LS_LAST_SHOWN, String(now));
  tlsSet(LS_LAST_PHRASE + triggerCase, String(phrase.id));
}

/** Invalide les caches (appelé après modification en page de paramétrage). */
function invalidateCache(): void {
  settingsCache = null;
  prefEnabledCache = null;
}

declare global {
  interface Window {
    OrnaxionMascot?: {
      onActionSaved: (resp: unknown) => void | Promise<void>;
      onPageOpened: (triggerCase: string) => void | Promise<void>;
      invalidateCache: () => void;
    };
  }
}

window.OrnaxionMascot = { onActionSaved, onPageOpened, invalidateCache };
